import React, { useState, useEffect } from 'react';
import { doctorAPI } from '../../services/api';
import { tokenManager } from '../../services/tokenManager';
import { FaEnvelope } from 'react-icons/fa';

// Utility: Debounce Hook
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debounced;
}

// Colors used for assessment indicators
const dotColors = [
  'bg-blue-500',
  'bg-purple-500',
  'bg-pink-500',
  'bg-yellow-500',
  'bg-indigo-500',
  'bg-gray-500',
];

// Skeleton Loader
const PatientCardSkeleton = () => (
  <div className="border border-gray-200 rounded-lg p-4 shadow-sm animate-pulse">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
      <div className="flex-1">
        <div className="h-4 bg-gray-200 rounded w-24 mb-1"></div>
        <div className="h-3 bg-gray-200 rounded w-16"></div>
      </div>
      <div className="h-3 bg-gray-200 rounded w-12"></div>
    </div>
    <div className="h-3 bg-gray-200 rounded w-32 mb-1"></div>
    <div className="h-3 bg-gray-200 rounded w-28 mb-1"></div>
    <div className="h-3 bg-gray-200 rounded w-20 mb-4"></div>
    <div className="flex gap-2">
      <div className="flex-1 h-8 bg-gray-200 rounded"></div>
      <div className="flex-1 h-8 bg-gray-200 rounded"></div>
    </div>
  </div>
);

// Patient Modal
const PatientProfileModal = ({ isOpen, onClose, patient }) => {
  if (!isOpen || !patient) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">Patient Profile</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 flex-1 overflow-y-auto">
          <div className="flex items-center gap-4 mb-6">
            <img src={patient.image} alt={patient.name} className="w-16 h-16 rounded-full object-cover" />
            <div>
              <h4 className="text-xl font-semibold text-gray-900">{patient.name}</h4>
              <p className="text-gray-600 text-xs">Patient ID: #{patient.id}</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                patient.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>{patient.status}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Contact Information</h5>
              <table className="min-w-full text-sm">
                <tbody>
                  <tr><td className="font-semibold pr-2 py-1 text-gray-600">Email</td>
                  <td className="py-1 text-gray-900">
                    <span className="bg-blue-100 text-blue-800 font-bold px-3 py-1 rounded-full inline-block">{patient.email}</span>
                  </td></tr>
                  {patient.rawEmail && patient.userEmail && patient.rawEmail !== patient.userEmail && (
                    <tr><td className="font-semibold pr-2 py-1 text-gray-600">Alt Email</td>
                    <td className="py-1 text-gray-900">{patient.rawEmail}</td></tr>
                  )}
                  {patient.address && (
                    <tr><td className="font-semibold pr-2 py-1 text-gray-600">Address</td>
                    <td className="py-1 text-gray-900">{patient.address}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div>
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Personal Information</h5>
              <table className="min-w-full text-sm">
                <tbody>
                  {patient.dateOfBirth && (
                    <tr><td className="font-semibold pr-2 py-1 text-gray-600">Date of Birth</td>
                    <td className="py-1 text-gray-900">{new Date(patient.dateOfBirth).toLocaleDateString()}</td></tr>
                  )}
                  {patient.gender && (
                    <tr><td className="font-semibold pr-2 py-1 text-gray-600">Gender</td>
                    <td className="py-1 text-gray-900">{patient.gender}</td></tr>
                  )}
                  <tr><td className="font-semibold pr-2 py-1 text-gray-600">Assessment Status</td>
                  <td className="py-1 text-gray-900">{patient.assessment}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <h5 className="text-lg font-semibold text-gray-900 mb-3">Session Information</h5>
            <table className="min-w-full text-sm bg-gray-50 rounded-lg">
              <tbody>
                <tr>
                  <td className="font-semibold pr-2 py-1 text-gray-600">Last Session</td>
                  <td className="py-1 text-gray-900">{patient.lastSession}</td>
                  <td className="font-semibold pr-2 py-1 text-gray-600 text-right">Total Sessions</td>
                  <td className="py-1 text-gray-900">{typeof patient.totalSessions !== 'undefined' ? patient.totalSessions : '-'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Patients Component
const Patients = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1, totalPages: 1, totalPatients: 0,
    hasNextPage: false, hasPrevPage: false, limit: 9
  });

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const debouncedSearch = useDebounce(searchText, 600); // ✅ debounce search

  const currentUser = tokenManager.getUser();
  const doctorId = currentUser?._id || currentUser?.id;

  // Fetch Patients
  const fetchPatients = async () => {
    if (!doctorId) return;
    setLoading(true);
    setError(null);

    const filters = {
      search: debouncedSearch.trim(), // ✅ use debounced search
      status: statusFilter,
      sortBy,
      sortOrder,
      page: pagination.currentPage,
      limit: pagination.limit
    };

    try {
      const response = await doctorAPI.getPatientsForDoctor(doctorId, filters);
      if (response.success) {
        const transformed = response.data.map((p, i) => ({
          id: p._id || p.user?._id,
          name: p.user?.name || p.name || 'Unknown',
          email: p.user?.email || p.email || 'No email',
          userEmail: p.user?.email,
          rawEmail: p.email,
          lastSession: p.lastSessionDate ? new Date(p.lastSessionDate).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric'
          }) : 'No sessions',
          assessment: p.assessmentStatus || 'Not Started',
          status: p.status || 'Active',
          image: p.user?.profileImage || p.profileImage || `https://i.pravatar.cc/150?img=${i + 10}`,
          phone: p.phone || 'No phone',
          dateOfBirth: p.dateOfBirth,
          gender: p.gender,
          address: p.address,
          totalSessions: p.totalSessions
        }));
        setPatients(transformed);

        if (response.pagination) {
          const { page, totalPages, total } = response.pagination;
          setPagination(prev => ({
            ...prev,
            currentPage: page || prev.currentPage,
            totalPages: totalPages || 1,
            totalPatients: total || 0,
            hasNextPage: page < totalPages,
            hasPrevPage: page > 1
          }));
        }
      } else {
        setError(response.message || 'Failed to fetch patients');
      }
    } catch (err) {
      setError(err.message || 'Error loading patients');
    } finally {
      setLoading(false);
    }
  };

  // Fetch patients on filter or pagination changes
  useEffect(() => {
    fetchPatients();
  }, [debouncedSearch, pagination.currentPage, statusFilter, sortBy, sortOrder]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSortChange = (e) => {
    const val = e.target.value;
    setSortBy(val);
    setSortOrder(val === 'name' ? 'asc' : 'desc');
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handleViewProfile = (id) => {
    const found = patients.find(p => p.id === id);
    if (found) {
      setSelectedPatient(found);
      setIsProfileModalOpen(true);
    }
  };

  return (
    <div className="min-h-screen px-4">
      <h2 className="text-2xl font-semibold text-gray-900">My Patients</h2>
      <p className="text-gray-600 mt-2 mb-6">Manage and track your current patients here.</p>

      {/* Search + Filters */}
<div className="flex flex-wrap items-center gap-3 mb-4">
  <div className="relative w-72">
    <input
      type="text"
      value={searchText}
      onChange={handleSearchChange}
      placeholder="Search by name or email"
      className="pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-full"
    />
    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    </div>
  </div>
  <select
    value={statusFilter}
    onChange={handleStatusChange}
    className="w-40 text-sm border border-gray-200 rounded-md px-3 p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white "
  >
    <option value="">Status: All</option>
    <option value="active">Active</option>
    <option value="inactive">Inactive</option>
  </select>
  <select
    value={sortBy}
    onChange={handleSortChange}
    className="w-48 text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
  >
    <option value="name">Sort by: Name</option>
    <option value="lastSession">Sort by: Last Session</option>
  </select>
</div>

      {/* Patient Cards */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => <PatientCardSkeleton key={i} />)}
        </div>
      ) : patients.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
          {patients.map((p, i) => (
            <div key={p.id} className="border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-2">
                <img src={p.image} alt={p.name} className="w-10 h-10 rounded-full" />
                <div><p className="font-semibold text-sm">{p.name}</p><p className="text-xs text-gray-500">#{p.id}</p></div>
                <span className={`ml-auto text-xs font-medium ${p.status === 'Active' ? 'text-blue-600' : 'text-gray-400'}`}>● {p.status}</span>
              </div>
              <p className="text-sm text-gray-600 mb-1">{p.email}</p>
              <p className="text-sm text-gray-500 mb-1">Last Session: {p.lastSession}</p>
              <div className="flex items-center gap-2 mb-4">
                <span className={`w-2 h-2 rounded-full ${dotColors[i % dotColors.length]}`}></span>
                <p className="text-sm text-gray-500">Assessment: {p.assessment}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleViewProfile(p.id)} className="flex-1 border border-blue-500 text-blue-600 text-sm font-medium py-1.5 rounded-md hover:bg-blue-50">View Profile</button>
                <button className="flex-1 bg-blue-500 text-white text-sm font-medium py-1.5 rounded-md hover:bg-blue-600">Schedule</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg font-medium text-gray-900">No patients found</p>
          <p className="mt-2">Try adjusting your filters or search term.</p>
        </div>
      )}

      {/* Pagination */}
      {!loading && patients.length > 0 && (
        <div className="flex w-full justify-end mt-6">
          {/* Previous Button */}
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={!pagination.hasPrevPage}
            className={`px-4 py-2 border rounded-l-md text-sm font-medium text-gray-700 bg-white ${pagination.hasPrevPage ? 'hover:bg-gray-100' : 'cursor-not-allowed'}`}
          >
            &lt;
          </button>

          {/* Page Number Buttons */}
          {[...Array(pagination.totalPages)].map((_, idx) => (
            <button
              key={idx + 1}
              onClick={() => handlePageChange(idx + 1)}
              className={`px-4 py-2 border text-sm font-medium rounded-none ${
                pagination.currentPage === idx + 1
                                            ? 'bg-blue-100 text-blue-600 font-medium'
                          : 'hover:bg-gray-100'
              }`}
            >
              {idx + 1}
            </button>
          ))}

          {/* Next Button */}
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={!pagination.hasNextPage}
            className={`px-4 py-2 border rounded-r-md text-sm font-medium text-gray-700 bg-white ${pagination.hasNextPage ? 'hover:bg-gray-100' : 'cursor-not-allowed'}`}
          >
            &gt;
          </button>
        </div>
      )}

      {/* Patient Profile Modal */}
      <PatientProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        patient={selectedPatient}
      />
    </div>
  );
};

export default Patients;
